import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { decideDeleteRequest, listAdminDeleteRequests } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";

// Deletion requests from every gym (Anish, 21 Oct 2026: no deleting data without an admin's
// approval). Approving deletes the record exactly as an admin's delete would (closed months and
// the other checks still apply); declining needs a note the gym sees. Both go in the audit trail.
const SEL = "bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-xs text-zinc-200";
const STATUS = { requested: "Waiting", approved: "Approved", declined: "Declined" };

export default function DeleteRequests({ onCount }) {
  const [rows, setRows] = useState(null);
  const [show, setShow] = useState("requested");
  const [gym, setGym] = useState("");
  const [kind, setKind] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await listAdminDeleteRequests();
      setRows(r);
      onCount?.(r.filter((x) => x.status === "requested").length);
    } catch (e) { toast.error(errMsg(e, "Could not load")); setRows([]); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const gyms = useMemo(() => [...new Map((rows || []).filter((r) => r.facility_id).map((r) => [r.facility_id, r.gym || r.facility_id])).entries()]
    .sort((a, b) => String(a[1]).localeCompare(String(b[1]))), [rows]);
  const kinds = useMemo(() => [...new Map((rows || []).map((r) => [r.kind, r.kind_label || r.kind])).entries()], [rows]);

  const decide = async (r, approve) => {
    const note = approve ? "" : window.prompt(`Decline deleting ${r.label}? Say why (the gym sees this).`);
    if (!approve && !note?.trim()) return;
    if (approve && !window.confirm(`Delete ${r.kind_label || r.kind}: ${r.label} at ${r.gym}? This can't be undone.`)) return;
    setBusy(r.id);
    try {
      await decideDeleteRequest(r.id, { approve, note: note || "" });
      toast.success(approve ? "Approved and deleted" : "Declined");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(""); }
  };

  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const shown = rows.filter((r) => (show === "all" || r.status === show) && (!gym || r.facility_id === gym) && (!kind || r.kind === kind));
  const waiting = rows.filter((r) => r.status === "requested").length;
  return (
    <div className="card-surface overflow-x-auto" data-testid="delete-requests">
      <div className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Deletion requests{waiting ? ` · ${waiting} waiting` : ""}</div>
          <div className="text-[11px] text-zinc-500 max-w-2xl">Only admins delete data. When anyone else deletes a consult, sale, payment, expense or another record, it comes here first. Approving deletes it as your own delete would; declining keeps it.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={show} onChange={(e) => setShow(e.target.value)} className={SEL} data-testid="delete-requests-status">
            <option value="requested">Waiting</option><option value="approved">Approved</option>
            <option value="declined">Declined</option><option value="all">All</option>
          </select>
          <select value={gym} onChange={(e) => setGym(e.target.value)} className={SEL} data-testid="delete-requests-gym">
            <option value="">All gyms</option>
            {gyms.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={SEL} data-testid="delete-requests-kind">
            <option value="">All types</option>
            {kinds.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
      </div>
      {shown.length === 0 ? <Empty>No requests here.</Empty> : (
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Requested</th><th className="px-3 py-2 font-medium">Gym</th>
            <th className="px-3 py-2 font-medium">What</th><th className="px-3 py-2 font-medium">Why</th>
            <th className="px-3 py-2 font-medium">Status</th><th />
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className={TR} data-testid={`delete-request-${r.id}`}>
                <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{String(r.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">{r.requested_by}</div></td>
                <td className="px-3 py-2 text-zinc-300">{r.gym || "—"}</td>
                <td className="px-3 py-2 text-xs"><div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">{r.kind_label || r.kind}</div>
                  <div className="text-zinc-200">{r.label}</div></td>
                <td className="px-3 py-2 text-xs text-zinc-300 max-w-[18rem]">{r.reason}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{STATUS[r.status] || r.status}
                  {r.decided_by && <div className="text-[10px] text-zinc-500">{r.decided_by}{r.decision_note ? `: ${r.decision_note}` : ""}</div>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "requested" && <>
                    <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => decide(r, true)} className="h-7 text-emerald-300" data-testid={`approve-delete-${r.id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => decide(r, false)} className="h-7 text-rose-300" data-testid={`decline-delete-${r.id}`}><X className="h-3.5 w-3.5 mr-1" />Decline</Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
